import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar, GraduationCap, Mail, MessageCircle, Phone, Stethoscope } from "lucide-react";
import { HeroFade, StaggerGrid, StaggerItem } from "@/lib/motion";
import { useSEO } from "@/hooks/useSEO";

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  useEffect(() => { api.get("/doctors").then(({ data }) => setDoctors(data)); }, []);

  useSEO({
    title: "Our Doctors & Specialists",
    description: "Meet the highly qualified medical specialists at Afzal Medical Complex, including experts in cardiology, surgery, paediatrics, gynecology, orthopaedics, and dentistry."
  });

  return (
    <div data-testid="doctors-page">
      <section className="section bg-red-50/40">
        <div className="container-medi max-w-3xl">
          <HeroFade><div className="overline mb-3">Our Doctors</div></HeroFade>
          <HeroFade delay={0.1}><h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-slate-900">Meet the specialists who care for you</h1></HeroFade>
          <HeroFade delay={0.2}><p className="mt-5 text-slate-600 text-lg">Highly qualified consultants across cardiology, surgery, paediatrics, gynaecology, orthopaedics, dentistry and more.</p></HeroFade>
        </div>
      </section>

      <section className="section">
        <StaggerGrid className="container-medi grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {doctors.map((d) => (
            <StaggerItem key={d.id}>
              <Link to={`/doctors/${d.id}`} className="card-medi group block" data-testid={`doctor-item-${d.id}`}>
                <div className="aspect-square overflow-hidden bg-slate-100">
                  {d.photo_url && <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                </div>
                <div className="p-5">
                  <div className="font-heading font-semibold text-slate-900">{d.name}</div>
                  <div className="text-sm text-red-700 mt-0.5">{d.specialty}</div>
                  <div className="text-xs text-slate-500 mt-2 line-clamp-1">{d.qualifications}</div>
                </div>
              </Link>
            </StaggerItem>
          ))}
          {doctors.length === 0 && <p className="text-slate-500">No doctors yet.</p>}
        </StaggerGrid>
      </section>
    </div>
  );
}

export function DoctorDetail() {
  const { id } = useParams();
  const { site } = useOutletContext();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/doctors/${id}`).then(({ data }) => setDoc(data)).catch(() => setErr("Doctor not found"));
  }, [id]);

  useSEO({
    title: doc ? `${doc.name} - ${doc.specialty}` : "Doctor Profile",
    description: doc ? `${doc.name} is a board-certified specialist in ${doc.specialty} at Afzal Medical Complex. Qualifications: ${doc.qualifications}. Bio: ${doc.bio?.substring(0, 150)}...` : "Load Doctor details...",
    schema: doc ? {
      "@context": "https://schema.org",
      "@type": "Physician",
      "name": doc.name,
      "image": doc.photo_url,
      "medicalSpecialty": doc.specialty,
      "description": doc.bio,
      "qualifications": doc.qualifications,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "PK",
        "streetAddress": "Main GT Road"
      },
      "telephone": "+923419300560",
      "worksFor": {
        "@type": "MedicalOrganization",
        "name": "Afzal Medical Complex",
        "url": "https://afzalmedicalcomplex.com"
      }
    } : null
  });

  if (err) return <div className="container-medi section">{err}</div>;
  if (!doc) return <div className="container-medi section text-slate-500">Loading…</div>;

  const whatsapp = site?.whatsapp || "923419300560";
  const waMsg = encodeURIComponent(`Assalam-o-Alaikum, I would like to book an appointment with ${doc.name} (${doc.specialty}). Please share available slots.`);

  return (
    <div data-testid="doctor-detail-page">
      <section className="section bg-red-50/40">
        <div className="container-medi">
          <Link to="/doctors" className="text-sm text-red-700 inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> All doctors</Link>
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-1">
              {doc.photo_url && <img src={doc.photo_url} alt={doc.name} className="w-full aspect-square rounded-3xl object-cover shadow-medi-lg" />}
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Stethoscope className="w-4 h-4 text-red-600" />
                <span className="overline">{doc.specialty}</span>
              </div>
              <h1 className="text-4xl font-heading font-semibold text-slate-900">{doc.name}</h1>
              <p className="mt-2 text-slate-600 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-red-600" /> {doc.qualifications}</p>
              <p className="mt-6 text-slate-600 leading-relaxed whitespace-pre-line">{doc.bio}</p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <div className="card-medi p-5">
                  <div className="flex items-center gap-2 text-red-700 text-sm font-medium"><Calendar className="w-4 h-4" /> Schedule</div>
                  <div className="mt-1 text-slate-800">{doc.schedule || "By appointment"}</div>
                </div>
                {doc.phone && (
                  <div className="card-medi p-5">
                    <div className="flex items-center gap-2 text-red-700 text-sm font-medium"><Phone className="w-4 h-4" /> Phone</div>
                    <div className="mt-1 text-slate-800">{doc.phone}</div>
                  </div>
                )}
                {doc.email && (
                  <div className="card-medi p-5">
                    <div className="flex items-center gap-2 text-red-700 text-sm font-medium"><Mail className="w-4 h-4" /> Email</div>
                    <div className="mt-1 text-slate-800">{doc.email}</div>
                  </div>
                )}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="book-appointment-cta">Book an Appointment</Link>
                <a href={`https://wa.me/${whatsapp}?text=${waMsg}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-full px-6 py-3 shadow-medi-md transition" data-testid="book-whatsapp">
                  <MessageCircle className="w-4 h-4" /> Book via WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
